/**
 * The Forma tab's left-hand list: every movement the client has logged, in R15's order.
 *
 * R15: most recent clip first, then most recent logged execution. A movement filmed yesterday rises
 * above one trained this morning and never filmed; two never filmed keep the order they were last
 * trained in. The backend sends the list unordered for this purpose, so the order is decided here.
 */

import {
  exerciseCatalogue,
  exerciseNameMatches,
  type ExerciseCatalogueView,
  type ExerciseListItem,
  type ExerciseQuery,
  type OtherLanguageNames,
} from '~/entities/exercise-library';
import type { Translate } from '~/shared/lib/localeTranslate';
import type { PreferenceStore } from '~/shared/lib/preferenceStore';

import { readTrainerFlag, writeTrainerFlag } from './trainerFlag';

export interface TrainedExerciseSource {
  readonly exerciseId: number;
  readonly name: string;
  readonly clipCount: number;
  readonly lastClipAt: number | null;
  readonly lastLoggedAt: number | null;
  readonly unread: boolean;
}

export interface FormaExerciseRow {
  readonly exerciseId: number;
  readonly name: string;
  /** "Trenowane 22 wrz", or `null` for a movement filmed but never logged. */
  readonly trained: string | null;
  readonly hasClip: boolean;
  readonly unread: boolean;
  readonly coverUrl: string | null;
}

const byRecency = (left: number | null, right: number | null): number =>
  (right ?? Number.NEGATIVE_INFINITY) - (left ?? Number.NEGATIVE_INFINITY);

function orderTrainedExercises(
  sources: readonly TrainedExerciseSource[],
): readonly TrainedExerciseSource[] {
  return [...sources].sort(
    (left, right) =>
      byRecency(left.lastClipAt, right.lastClipAt) ||
      byRecency(left.lastLoggedAt, right.lastLoggedAt) ||
      left.name.localeCompare(right.name, 'pl'),
  );
}

/** „Z nagraniem": only the movements with a recording, in the list's own order. */
export function exerciseRowsShown(
  rows: readonly FormaExerciseRow[],
  withClipOnly: boolean,
): readonly FormaExerciseRow[] {
  return withClipOnly ? rows.filter((row) => row.hasClip) : rows;
}

export interface FormaListNarrowing {
  /** The library's search and four facets, as the trainer set them. */
  readonly query: ExerciseQuery;
  /** „Z nagraniem". */
  readonly withClipOnly: boolean;
}

export interface FormaNarrowedList {
  /** The rows still standing, in R15's order — the query never re-sorts the list. */
  readonly rows: readonly FormaExerciseRow[];
  /** The facets over the client's OWN movements: an option no trained movement carries is not offered. */
  readonly catalogue: ExerciseCatalogueView;
  /**
   * What narrows the list, which decides what an empty one says: `query` — the search or a facet —
   * is „nothing matches" whether or not „Z nagraniem" is on too; `withClip` alone is „nothing is
   * filmed yet".
   */
  readonly narrowedBy: 'nothing' | 'withClip' | 'query';
}

/**
 * The list the trainer sees: the search, the facets and „Z nagraniem" together.
 *
 * The search and facets are the exercise library's own (`exerciseCatalogue`: Polish folding, OR
 * within a facet, AND across them), run over the catalogue's description of the movements the list
 * shows, so a facet's count is what „Pokaż N" will show. A movement the catalogue has not described
 * (its read pending or failed, or the movement since deleted) is still found by its own name, and
 * the facets do not narrow it: there is nothing to judge it by, and hiding it would say it does not
 * match. A described movement is also found by its name in the other content language
 * (`otherNames`), exactly as the library finds it.
 */
export function narrowExerciseList(
  rows: readonly FormaExerciseRow[],
  described: readonly ExerciseListItem[],
  { query, withClipOnly }: FormaListNarrowing,
  locale: string,
  otherNames?: OtherLanguageNames,
): FormaNarrowedList {
  const shown = exerciseRowsShown(rows, withClipOnly);
  const shownIds = new Set(shown.map((row) => row.exerciseId));
  const catalogue = exerciseCatalogue(
    described.filter((item) => shownIds.has(item.id)),
    query,
    locale,
    otherNames,
  );
  const asked = query.text.trim() !== '' || catalogue.activeCount > 0;
  const describedIds = new Set(described.map((item) => item.id));
  const matched = new Set(catalogue.results.map((item) => item.id));
  const standing = (row: FormaExerciseRow) =>
    describedIds.has(row.exerciseId)
      ? matched.has(row.exerciseId)
      : exerciseNameMatches(row.name, query.text);
  return {
    rows: asked ? shown.filter(standing) : shown,
    catalogue,
    narrowedBy: asked ? 'query' : withClipOnly ? 'withClip' : 'nothing',
  };
}

/**
 * „Z nagraniem" after „Wyczyść filtry", which always clears the search and the facets. The chip goes
 * only when it alone emptied the list, and then for this visit: the saved preference is the
 * trainer's, and clearing a search is not a vote against it.
 */
export function withClipAfterClearing(
  withClipOnly: boolean,
  narrowedBy: FormaNarrowedList['narrowedBy'],
): boolean {
  return narrowedBy === 'withClip' ? false : withClipOnly;
}

const WITH_CLIP_ONLY_KEY = 'movebuddy.forma.withClipOnly';

/** „Z nagraniem" is remembered per trainer on this device. */
export function readWithClipOnly(store: PreferenceStore | null, trainerId: string | null): boolean {
  return readTrainerFlag(store, WITH_CLIP_ONLY_KEY, trainerId);
}

export function writeWithClipOnly(store: PreferenceStore | null, trainerId: string | null, on: boolean): void {
  writeTrainerFlag(store, WITH_CLIP_ONLY_KEY, trainerId, on);
}

export interface FormaExerciseDeps {
  readonly t: Translate;
  readonly locale: string;
}

export function buildExerciseList(
  sources: readonly TrainedExerciseSource[],
  covers: ReadonlyMap<number, string | null>,
  { t, locale }: FormaExerciseDeps,
): readonly FormaExerciseRow[] {
  const dayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  return orderTrainedExercises(sources).map((source) => ({
    exerciseId: source.exerciseId,
    name: source.name,
    trained:
      source.lastLoggedAt === null
        ? null
        : t('web:forma.list.trained', { date: dayMonth.format(new Date(source.lastLoggedAt)) }),
    hasClip: source.clipCount > 0,
    unread: source.unread,
    coverUrl: covers.get(source.exerciseId) ?? null,
  }));
}
